import { type PrecacheEntry, Serwist, type SerwistGlobalConfig } from 'serwist';

import {
  isLegacyPrivateCache,
  isOfflineAssetPath,
  OFFLINE_FALLBACK_CACHE,
  OFFLINE_PAGE_PATH,
  shouldBypassServiceWorker,
} from '../kernel/offline/swNavigationPolicy';

declare global {
  interface WorkerGlobalScope extends SerwistGlobalConfig {
    __SW_MANIFEST: (PrecacheEntry | string)[] | undefined;
  }
}

declare const self: WorkerGlobalScope & typeof globalThis;

type FetchEventLike = {
  request: Request;
  stopImmediatePropagation: () => void;
  respondWith: (response: Promise<Response> | Response) => void;
  waitUntil: (promise: Promise<unknown>) => void;
};

type ExtendableEventLike = {
  waitUntil: (promise: Promise<unknown>) => void;
};

function isExtendableEvent(event: Event): event is Event & ExtendableEventLike {
  return 'waitUntil' in event && typeof event.waitUntil === 'function';
}

function isFetchEvent(event: Event): event is Event & FetchEventLike {
  return (
    'request' in event &&
    event.request instanceof Request &&
    'respondWith' in event &&
    typeof event.respondWith === 'function' &&
    'waitUntil' in event &&
    typeof event.waitUntil === 'function'
  );
}

const SW_DEBUG_ENABLED = process.env.NEXT_PUBLIC_DEBUG_SERWIST === '1';

function precacheSummary(): string {
  const m = self.__SW_MANIFEST;
  if (m === undefined) return 'undefined';
  if (Array.isArray(m)) return `${m.length} entries`;
  return 'non-array';
}

function swDebug(...args: unknown[]) {
  if (!SW_DEBUG_ENABLED) return;
  console.log('[crm-sw-debug]', ...args);
}

swDebug('worker evaluating', { precache: precacheSummary() });

const NAVIGATION_RETRY_DELAY_MS = 400;
// A failed candidate installation cannot replace the active worker's shell.
const shellCacheName = crypto.subtle
  .digest('SHA-256', new TextEncoder().encode(JSON.stringify(self.__SW_MANIFEST ?? [])))
  .then(
    (digest) =>
      `${OFFLINE_FALLBACK_CACHE}-${Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')}`
  );

// A rejected fetch while the browser reports online is usually a dead pooled
// connection (e.g. ERR_QUIC_PROTOCOL_ERROR), not an outage; the browser marks
// it broken, so one retry goes out on a fresh connection.
async function fetchNavigation(request: Request): Promise<Response> {
  const retryRequest = request.clone();
  try {
    return await fetch(request);
  } catch (error) {
    if (!self.navigator.onLine || request.method !== 'GET') throw error;
    await new Promise((resolve) => setTimeout(resolve, NAVIGATION_RETRY_DELAY_MS));
    return fetch(retryRequest);
  }
}

async function handleOfflineNavigation(request: Request): Promise<Response> {
  try {
    const response = await fetchNavigation(request);
    if (response.status < 500) return response;
  } catch {
    /* An unavailable server can use only the content-free offline entry. */
  }
  const cache = await caches.open(await shellCacheName);
  return (await cache.match(OFFLINE_PAGE_PATH)) ?? new Response('Offline', { status: 503 });
}

self.addEventListener('install', (evt) => {
  if (!isExtendableEvent(evt)) throw new Error('Install event lacks waitUntil');
  const event = evt;
  swDebug('install', { precache: precacheSummary() });
  event.waitUntil(
    (async () => {
      const response = await fetch(OFFLINE_PAGE_PATH, { credentials: 'omit', cache: 'no-store', redirect: 'error' });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html'))
        throw new Error('Offline entry is unavailable');
      const cache = await caches.open(await shellCacheName);
      await cache.put(OFFLINE_PAGE_PATH, response);
    })()
  );
});

self.addEventListener('activate', (event) => {
  if (!isExtendableEvent(event)) return;
  event.waitUntil(
    (async () => {
      for (const name of await caches.keys()) {
        if (isLegacyPrivateCache(name) || (name.startsWith(OFFLINE_FALLBACK_CACHE) && name !== (await shellCacheName)))
          await caches.delete(name);
      }
    })()
  );
});

self.addEventListener('message', (event) => {
  if (!('data' in event) || event.data !== 'CLINICAL_OFFLINE_READY' || !('ports' in event)) return;
  const message = event as MessageEvent;
  // Activation follows successful installation of both precache assets and the neutral shell.
  const check = async () => {
    const shell = await (await caches.open(await shellCacheName)).match(OFFLINE_PAGE_PATH);
    const urls = serwist.getPrecachedUrls();
    const present = await Promise.all(urls.map((url) => serwist.matchPrecache(url)));
    message.ports[0]?.postMessage({ ready: Boolean(shell && urls.length && present.every(Boolean)), version: 1 });
  };
  if (isExtendableEvent(event))
    event.waitUntil(check().catch(() => message.ports[0]?.postMessage({ ready: false, version: 1 })));
});

// Auth/OAuth/SSE stay native (no respondWith). Other navigations pass through
// fetch(event.request) so Set-Cookie and opaqueredirect still work; Serwist
// defaultCache must not synthesize those fetches (cookie-stripping, 2026-04).
self.addEventListener('fetch', (evt) => {
  if (!isFetchEvent(evt)) throw new Error('Fetch event lacks service worker methods');
  const event = evt;
  if (shouldBypassServiceWorker(event.request, self.location.origin)) {
    swDebug('bypass SW', event.request.mode, event.request.url);
    event.stopImmediatePropagation();
    return;
  }

  if (event.request.mode === 'navigate') {
    event.stopImmediatePropagation();
    event.respondWith(handleOfflineNavigation(event.request));
    return;
  }
});

const serwist = new Serwist({
  precacheEntries: self.__SW_MANIFEST?.filter((entry) => {
    const path = new URL(typeof entry === 'string' ? entry : entry.url, self.location.origin).pathname;
    return isOfflineAssetPath(path);
  }),
  skipWaiting: true,
  clientsClaim: true,
  navigationPreload: false,
  runtimeCaching: [],
});

// A failed runtime-cache fetch with nothing cached is an ordinary network
// error for the page; without this Serwist rejects respondWith and logs an
// uncaught no-response in the worker on every such request.
serwist.setCatchHandler(async () => Response.error());

serwist.addEventListeners();
